import CitationBadge from './CitationBadge';
import type { CitationViewerTarget } from './CitationDocumentViewer';
import { citationSource, CitedSource, WORKFLOW_ATTACHABLE_MODEL } from './useCitationViewer';

/**
 * One numbered badge per cited document of a referral: hover previews the cited page with the
 * passage outlined, and clicking opens the document beside the referral at it. A source that isn't
 * a PDF opens in a new tab instead.
 *
 * A document no longer on the referral gets no badge: there is nothing of the referral's to open,
 * and a numbered chip that does nothing on hover or click reads as broken.
 *
 * Badges only, no container: each surface lays them out in its own row.
 */
export default function SourceBadges({
  sources,
  attachments,
  onOpen,
}: {
  sources: CitedSource[];
  attachments: { id: string; filename: string | null }[];
  onOpen: (target: CitationViewerTarget) => void;
}) {
  const filenames = new Map(attachments.map((attachment) => [attachment.id, attachment.filename]));
  return (
    <>
      {sources.flatMap((source, index) => {
        if (!filenames.has(source.attachmentId)) return [];
        const { label, target } = citationSource(source, filenames.get(source.attachmentId));
        return (
          <CitationBadge
            // By index as well as id: the same document can legitimately be cited twice.
            key={`${source.attachmentId}-${index}`}
            attachment={{ attachmentId: source.attachmentId, displayId: source.displayId, filename: label }}
            attachmentType={WORKFLOW_ATTACHABLE_MODEL}
            pages={source.pages}
            highlights={source.highlights}
            onClick={target ? () => onOpen(target) : undefined}
            variant='white'
            size={14}
          />
        );
      })}
    </>
  );
}
